/**
 * The contacts we hold at a partner, whichever side of the trade it is on.
 *
 * The customer detail page and the provider detail page share it: both read a nested
 * ``contacts/`` endpoint with the same shape, and both offer the same three moves on
 * what the partner publishes (see, import, refresh). Only the URL and the permission
 * that guards it differ, so they are options.
 */

import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 50, ordering: "last_name", active: true };

const createState = (options = {}) => ({
    contacts: [],
    contacts_url: null,
    contacts_filters: DEFAULT_FILTERS,
    contacts_count: 0,
    contacts_loaded: false,
    contacts_loading: false,
    contacts_loading_errors: null,
    contact_saving: false,
    contact_saving_errors: {},
    // Contacts the connected partner publishes: what we may copy into our own book.
    available_contacts: [],
    available_contacts_loaded: false,
    available_contacts_loading: false,
    contacts_importing: false,
    cancelTokenSource: null,
    _viewPermission: options?.permission || "core.view_customer",
    _storageOptions: options || {},
});

function canViewContacts(state) {
    const permissions = store.getters["session/current_user_permissions"];
    return permissions.indexOf(state._viewPermission) !== -1;
}

const mutations = {
    updateContacts(state, contacts) {
        state.contacts = contacts;
        state.contacts_loaded = true;
        state.contacts_loading = false;
        state.contacts_loading_errors = null;
    },
    updateContactsCount(state, contacts_count) {
        state.contacts_count = contacts_count;
    },
    updateContactsUrl(state, contacts_url) {
        state.contacts_url = contacts_url;
    },
    updateContactsLoading(state, contacts_loading) {
        state.contacts_loading = contacts_loading;
    },
    updateContactsLoadingErrors(state, contacts_loading_errors) {
        state.contacts_loading_errors = contacts_loading_errors;
        state.contacts_loading = false;
    },
    updateContactsFilters(state, filters) {
        state.contacts_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.contacts_filters, fieldsToSave);
        }
    },
    updateContactSaving(state, contact_saving) {
        state.contact_saving = contact_saving;
    },
    updateContactSavingErrors(state, contact_saving_errors) {
        state.contact_saving_errors = contact_saving_errors || {};
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
    updateAvailableContacts(state, available_contacts) {
        state.available_contacts = available_contacts;
        state.available_contacts_loaded = true;
        state.available_contacts_loading = false;
    },
    updateAvailableContactsLoading(state, available_contacts_loading) {
        state.available_contacts_loading = available_contacts_loading;
    },
    resetAvailableContacts(state) {
        state.available_contacts = [];
        state.available_contacts_loaded = false;
    },
    updateContactsImporting(state, contacts_importing) {
        state.contacts_importing = contacts_importing;
    },
};

const actions = {
    async fetchContacts({ commit, state }) {
        if (!state.contacts_url) {
            return;
        }
        if (!canViewContacts(state)) {
            commit("updateContacts", []);
            commit("updateContactsCount", 0);
            return;
        }

        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateContactsLoading', true);
        commit('updateContactsLoadingErrors', null);

        try {
            const response = await axios.get(state.contacts_url, {
                params: state.contacts_filters,
                cancelToken: source.token,
            });
            commit('updateContacts', response.data.results);
            commit('updateContactsCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateContactsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateContactsLoading', false);
        }
    },

    async saveContact({ commit, dispatch, state }, params) {
        commit('updateContactSaving', true);
        commit('updateContactSavingErrors', {});
        try {
            const contact = params.contact;
            const response = contact.id
                ? await axios.patch(`${state.contacts_url}${contact.id}/`, contact)
                : await axios.post(state.contacts_url, contact);
            await dispatch('fetchContacts');
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateContactSavingErrors', error.details);
            throw error;
        } finally {
            commit('updateContactSaving', false);
        }
    },

    async deleteContact({ dispatch, state }, params) {
        try {
            const response = await axios.delete(`${state.contacts_url}${params.contact.id}/`);
            await dispatch('fetchContacts');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async setPrimaryContact({ dispatch, state }, params) {
        try {
            const response = await axios.post(
                `${state.contacts_url}${params.contact.id}/set-primary/`
            );
            await dispatch('fetchContacts');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchAvailableContacts({ commit, state }) {
        if (!state.contacts_url) {
            return;
        }
        if (!canViewContacts(state)) {
            commit("updateAvailableContacts", []);
            return;
        }

        commit('updateAvailableContactsLoading', true);
        try {
            const response = await axios.get(`${state.contacts_url}available/`);
            commit('updateAvailableContacts', response.data.results);
            return response.data.results;
        } catch (xhr_error) {
            commit('updateAvailableContacts', []);
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateAvailableContactsLoading', false);
        }
    },

    async importContacts({ commit, dispatch, state }, params) {
        commit('updateContactsImporting', true);
        try {
            const response = await axios.post(`${state.contacts_url}import/`, {
                contacts: params?.contacts || [],
            });
            await dispatch('fetchContacts');
            await dispatch('fetchAvailableContacts');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateContactsImporting', false);
        }
    },

    async refreshContact({ dispatch, state }, params) {
        try {
            const response = await axios.post(
                `${state.contacts_url}${params.contact.id}/refresh/`
            );
            await dispatch('fetchContacts');
            await dispatch('fetchAvailableContacts');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit, state }, params) {
        commit('updateContactsUrl', params?.url || null);

        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }

        await commit('updateContactsFilters', {
            ...DEFAULT_FILTERS,
            ...(state._storageOptions?.storageKey
                ? loadFiltersFromStorage(state._storageOptions?.storageKey, {})
                : {}),
            ...(params?.filters || {}),
        });

        commit('updateContacts', []);
        commit('resetAvailableContacts');
    },
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});
